import { EditIcon } from "@xola/icons";
import { Button, getSymbol, Input, Popover, Select } from "@xola/ui-kit";
import clsx from "clsx";
import { isNil } from "lodash";
import React, { useEffect, useState } from "react";
import { GratuityAlgorithm } from "types";
import { SecondaryButton } from "../Common/SecondaryButton";
import { Currency } from "@/components/Common/Currency";
import { useModal } from "@/hooks/useVisibility";
import { GratuitySelection } from "@/stores/createPurchaseStore";
import { useLocalizationStore } from "@/stores/localizationStore";
import { getSeller } from "@/utils/getSeller";

interface CustomGratuitySelectorProps {
    itemUnitSubtotal: number;
    selection?: GratuitySelection;
    onSelection: (amount: number, algorithm: GratuityAlgorithm, custom: boolean) => void;
}

interface TemporaryGratuityValue {
    amount: number | null;
    algorithm: GratuityAlgorithm;
}

export const CustomGratuitySelector = ({ itemUnitSubtotal, selection, onSelection }: CustomGratuitySelectorProps) => {
    const customGratuityPopover = useModal(false);
    const [temporaryValue, setTemporaryValue] = useState<TemporaryGratuityValue>({
        amount: null,
        algorithm: GratuityAlgorithm.ABSOLUTE,
    });

    const seller = getSeller();
    const locale = useLocalizationStore().getLocale();
    const sellerCurrencySymbol = getSymbol(seller.currency, locale);

    useEffect(() => {
        if (selection?.custom) {
            setTemporaryValue({
                amount: selection.amount,
                algorithm: selection.algorithm,
            });
        } else {
            setTemporaryValue({
                amount: null,
                algorithm: GratuityAlgorithm.ABSOLUTE,
            });
        }
    }, [selection]);

    const handleApply = () => {
        if (!isNil(temporaryValue.amount)) {
            onSelection(temporaryValue.amount, temporaryValue.algorithm, true);
            customGratuityPopover.hide();
        }
    };

    const isSelected = selection?.custom === true;

    return (
        <div className="relative inline-block">
            <Popover
                placement="bottom"
                maxWidth={450}
                visible={customGratuityPopover.isVisible}
                onClickOutside={customGratuityPopover.hide}
            >
                <SecondaryButton
                    className={clsx(
                        "w-28",
                        isSelected ? "border border-blue-light bg-blue-lighter pr-2 hover:bg-blue-lighter" : "",
                    )}
                    onClick={customGratuityPopover.toggle}
                >
                    {isSelected ? (
                        <div className="space-x-4">
                            {selection.algorithm === GratuityAlgorithm.PERCENT ? (
                                <>
                                    {selection?.amount}%{" "}
                                    <span className="text-sm font-medium text-gray-dark">
                                        (<Currency>{selection.amount * 0.01 * itemUnitSubtotal}</Currency>)
                                    </span>
                                </>
                            ) : (
                                <Currency>{selection?.amount}</Currency>
                            )}
                            <EditIcon />
                        </div>
                    ) : (
                        "Custom"
                    )}
                </SecondaryButton>
                <Popover.Content className="space-y-1 p-2">
                    <div className="text-sm font-bold">Amount</div>
                    <div className="inline-flex items-center">
                        <Input
                            className="w-32 rounded-r-none"
                            type="number"
                            min={0}
                            value={temporaryValue.amount}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                setTemporaryValue({
                                    ...temporaryValue,
                                    amount: isNil(e.target.value) ? null : Number(e.target.value),
                                })
                            }
                        />
                        <Select
                            name="algorithm"
                            className="border-l-none !w-16 flex-none rounded-l-none leading-p1"
                            value={temporaryValue.algorithm}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                                setTemporaryValue({
                                    ...temporaryValue,
                                    algorithm: e.target.value as GratuityAlgorithm,
                                })
                            }
                        >
                            <option value={GratuityAlgorithm.ABSOLUTE}>{sellerCurrencySymbol}</option>
                            <option value={GratuityAlgorithm.PERCENT}>%</option>
                        </Select>
                    </div>
                    <div className="flex space-x-1 pt-1">
                        <SecondaryButton className="w-1/3" onClick={customGratuityPopover.toggle}>
                            Cancel
                        </SecondaryButton>
                        <Button className="w-2/3" disabled={isNil(temporaryValue.amount)} onClick={handleApply}>
                            Apply
                        </Button>
                    </div>
                </Popover.Content>
            </Popover>
        </div>
    );
};
